// Cross-process domain types. These are *response* shapes — request shapes
// live in `../schemas/`, where Zod is the source of truth.
import { z } from 'zod';
import { attachmentSchema } from '../schemas/attachment.js';
import { userStatusSchema, dmPrivacySchema } from '../schemas/userEnums.js';
import type { MessageSystemEvent } from '../schemas/message.js';

export type UserStatus = z.infer<typeof userStatusSchema>;

/**
 * Who may open a DM with this user. `EVERYONE` is the default; `FRIENDS`
 * restricts new 1:1 DMs to accepted friends (people you already share a
 * channel with are unaffected); `NOBODY` blocks all new 1:1 DMs. Existing
 * conversations and shared servers are unaffected by any tier.
 */
export type DmPrivacy = z.infer<typeof dmPrivacySchema>;

/**
 * Friendship lifecycle. A single directed row carries the state:
 *   PENDING  — requester asked, addressee hasn't answered yet
 *   ACCEPTED — both are friends (symmetric once accepted)
 * Declining or removing deletes the row (no tombstone).
 */
export type FriendshipStatus = 'PENDING' | 'ACCEPTED';

/**
 * Public profile card resolved by username — backs the `/u/:username` web page
 * and the mobile QR flow. Deliberately minimal: no platformId, no presence
 * (the endpoint is unauthenticated, so it must not leak more than a Telegram
 * `t.me/<name>` page would).
 */
export interface PublicUserProfile {
  id: string;
  username: string;
  displayName: string | null;
  avatarUrl: string | null;
  bio: string | null;
}

/**
 * A user-search hit (`/api/users/search`) — the public card plus the caller's
 * relationship, so the result row can show an "add friend / pending / friends"
 * affordance without a second round-trip.
 */
export interface UserSearchResult extends PublicUserProfile {
  friendState: FriendState;
}

/** One server the caller and a target user both belong to — the "общие
 *  серверы" strip in the profile card. Shared so the server select and the
 *  client consumer can't drift structurally. */
export interface MutualGuildItem {
  id: string;
  name: string;
  iconUrl: string | null;
}

/** Minimal user shape returned by the friends endpoints. */
export interface FriendUser {
  id: string;
  platformId: string;
  username: string;
  displayName: string | null;
  avatarUrl: string | null;
  status: UserStatus;
}

/** An accepted friend. */
export interface FriendEntry {
  user: FriendUser;
  since: string;
}

/** A pending friend request, from the current user's point of view. */
export interface FriendRequestEntry {
  /** The other party (sender for incoming, recipient for outgoing). */
  user: FriendUser;
  createdAt: string;
}

/** Full snapshot returned by GET /api/me/friends. */
export interface FriendsSnapshot {
  friends: FriendEntry[];
  incoming: FriendRequestEntry[];
  outgoing: FriendRequestEntry[];
}

/** Relationship of another user to the current user (for profile cards). */
export type FriendState = 'none' | 'friends' | 'incoming' | 'outgoing';

/**
 * Telegram-style DM folder: a per-user organisational view over the user's
 * existing DM channels. `channelIds` is ordered by the folder's own entry
 * positions. There's no socket fan-out — the only consumer is the same
 * user's other tabs/devices, which refetch on connect.
 */
export interface DmFolder {
  id: string;
  name: string;
  position: number;
  channelIds: string[];
}

/** Response shape of GET /api/me/dm-folders. */
export interface DmFoldersSnapshot {
  folders: DmFolder[];
}

/**
 * Discord-style server folder. Per-user grouping over guild memberships;
 * `guildIds` is ordered. `position` orders the folder in the top-level rail
 * alongside ungrouped guilds. No socket fan-out (other tabs refetch).
 */
export interface GuildFolder {
  id: string;
  name: string | null;
  color: string | null;
  position: number;
  guildIds: string[];
}

/** Response shape of GET /api/me/guild-folders. */
export interface GuildFoldersSnapshot {
  folders: GuildFolder[];
}

export type ChannelType =
  | 'CATEGORY'
  | 'TEXT'
  | 'VOICE'
  | 'DM'
  | 'GROUP_DM'
  | 'ANNOUNCE'
  | 'THREAD';

export type MuteLevel = 'ALL' | 'ALL_SILENT' | 'MENTIONS' | 'NOTHING';

export interface MentionUser {
  id: string;
  platformId: string;
  username: string;
  displayName: string | null;
}

export interface ApiErrorBody {
  error: string;
  message?: string;
  details?: unknown;
}

// ---------------------------------------------------------------------------
// Message DTOs
// ---------------------------------------------------------------------------
// These shapes live on the wire (server → client via REST + WS). Codex's
// review pointed out that the server has its own Prisma-derived types and
// the client redeclares the same fields in `client/src/types/api.ts` —
// risking drift on every schema change. Move them here so both sides
// import once.

export interface MessageAuthor {
  id: string;
  platformId: string;
  username: string;
  displayName: string | null;
  avatarUrl: string | null;
  /** cispracs.ru team tag, e.g. "NAVI" / "VP". Render alongside the name. */
  teamTag?: string | null;
}

export interface MessageReactionGroup {
  emoji: string;
  count: number;
  users: string[];
}

export interface ReplyTargetSummary {
  id: string;
  content: string;
  deletedAt: string | null;
  author: { id: string; username: string; displayName: string | null };
}

export type MessageAttachment = z.infer<typeof attachmentSchema>;

export interface PollOption {
  id: string;
  label: string;
  order: number;
  voteCount: number;
  voters: string[];
}

/**
 * Server-normalised poll attached to a message.
 * `voters` is empty on anonymous polls (the server stores them but
 * hides the list); the count is always real. `myOptionIds` is the list
 * of options the caller voted for, used by the UI to render a check.
 */
export interface PollSummary {
  id: string;
  question: string;
  multiChoice: boolean;
  anonymous: boolean;
  closedAt: string | null;
  createdAt: string;
  options: PollOption[];
  totalVoters: number;
  myOptionIds: string[];
}

export interface Message {
  id: string;
  channelId: string;
  authorId: string;
  content: string;
  /** Either a MessageAttachment[] or null. We accept `unknown` for safety
   *  with legacy rows; renderer normalizes via `parseAttachments`. */
  attachments: unknown;
  mentions: string[];
  replyToId: string | null;
  /** Parent-message summary (null when there's no reply or the parent is gone). */
  replyTo: ReplyTargetSummary | null;
  pinned: boolean;
  editedAt: string | null;
  deletedAt: string | null;
  createdAt: string;
  /** Service message (task-completion note etc.) — render as a centered line. */
  system?: boolean;
  /** What a service message records (rendered per language by the client);
   *  null/absent on ordinary messages and on older service rows, whose
   *  `content` is the text to show. */
  systemEvent?: MessageSystemEvent | null;
  author: MessageAuthor;
  reactions: MessageReactionGroup[];
  /** Optional poll attached to this message. Null on plain messages. */
  poll?: PollSummary | null;
  /** Telegram-style comment threads. On a comment: the root message id it
   *  hangs off. On a root: denormalised reply count + last-reply time +
   *  whether the thread is closed to new comments. Absent on legacy rows. */
  threadRootId?: string | null;
  threadReplyCount?: number;
  threadLastReplyAt?: string | null;
  threadClosed?: boolean;
  /** Up to 3 most-recent distinct commenters on this root message — drives the
   *  Telegram-style avatar stack on the "N комментариев" affordance. */
  threadParticipants?: ThreadParticipant[];
  /** Telegram-style task list attached to this message (null on plain msgs). */
  taskList?: TaskListSummary | null;
}

export interface TaskItem {
  id: string;
  label: string;
  order: number;
  /** Who checked it off (null = not done). Drives the "✓ by X" line. */
  completedBy: {
    id: string;
    username: string;
    displayName: string | null;
    avatarUrl: string | null;
  } | null;
  completedAt: string | null;
}

export interface TaskListSummary {
  id: string;
  title: string;
  /** Any channel member may toggle items (else only the author). */
  allowComplete: boolean;
  /** Members may append new items after publish. */
  allowAdd: boolean;
  items: TaskItem[];
  doneCount: number;
  total: number;
}

export interface ThreadParticipant {
  id: string;
  username: string;
  displayName: string | null;
  avatarUrl: string | null;
}

// ---------------------------------------------------------------------------
// Follow-ups ("Ответить позже") — personal reminders to revisit a message.
// See packages/shared/src/schemas/followUps.ts for the input schemas.
// ---------------------------------------------------------------------------

/** Where a followed-up message lives — drives the "where" label + navigation
 *  in the inbox without the client re-fetching the channel. Built server-side. */
export interface FollowUpLocation {
  channelId: string;
  /** How to label and navigate the source. */
  kind: 'dm' | 'group' | 'channel';
  /** Peer display name (DM), group name, or "#channel" (guild channel). */
  title: string;
  /** Set when kind === 'channel' so the UI can route into the guild. */
  guildId: string | null;
  /** Server / channel name shown as the secondary line for guild channels. */
  guildName: string | null;
  /** Avatar hints for the row (DM peer or guild icon); initials/color fall back. */
  avatarUrl: string | null;
  initials: string;
  color: string | null;
}

/** One follow-up as returned to its owner. `message` is a live snapshot of the
 *  target (null if it has since been deleted — the row still shows so the user
 *  can dismiss it). Status is `'pending' | 'done'`; everything time-related
 *  (overdue / today / scheduled / no-time) is derived from `remindAt`. */
export interface FollowUpItem {
  id: string;
  messageId: string;
  channelId: string;
  /** ISO instant the reminder fires, or null = no time (inbox-only). */
  remindAt: string | null;
  note: string | null;
  status: 'pending' | 'done';
  createdAt: string;
  completedAt: string | null;
  message: Message | null;
  location: FollowUpLocation;
}

export interface FollowUpsSnapshot {
  items: FollowUpItem[];
}

/** A vault shelf ("полка") with how many saved items it holds. */
export interface VaultShelf {
  id: string;
  name: string;
  count: number;
}

export interface VaultShelvesSnapshot {
  shelves: VaultShelf[];
}

// ---------------------------------------------------------------------------
// Channel / Guild / DM DTOs
// ---------------------------------------------------------------------------

/** Bit flags for Channel.memberPermissions — controls what MEMBER-role
 *  participants of a GROUP_DM can post.  The server stores this as a BIGINT;
 *  we keep it here so client and server agree on the bit positions. */
export const GROUP_DM_PERMISSIONS = {
  SEND_MESSAGES: 1,
  SEND_MEDIA: 2,
  SEND_VOICE_MSG: 4,
  SEND_FILES: 8,
  PIN_MESSAGES: 16,
  ADD_USERS: 32,
} as const;

export const GROUP_DM_PERMISSIONS_ALL =
  GROUP_DM_PERMISSIONS.SEND_MESSAGES |
  GROUP_DM_PERMISSIONS.SEND_MEDIA |
  GROUP_DM_PERMISSIONS.SEND_VOICE_MSG |
  GROUP_DM_PERMISSIONS.SEND_FILES |
  GROUP_DM_PERMISSIONS.PIN_MESSAGES |
  GROUP_DM_PERMISSIONS.ADD_USERS;

export type GroupParticipantRole = 'OWNER' | 'ADMIN' | 'MEMBER';

export interface ChannelPermissionOverride {
  channelId: string;
  roleId: string;
  allow: string;
  deny: string;
}

export interface Channel {
  id: string;
  guildId: string | null;
  type: ChannelType;
  /** Guild channels: my effective permission bits in it (decimal string) —
   *  whether I may write, attach, react, join its voice. Absent on DMs. */
  myPermissions?: string;
  name: string | null;
  topic: string | null;
  /** GROUP_DM avatar URL (null for everything else). */
  iconUrl?: string | null;
  position: number;
  parentId: string | null;
  bitrateKbps: number | null;
  userLimit: number | null;
  slowmodeS: number | null;
  /** GROUP_DM permission bitmask for MEMBER role. Default 63 (all on). */
  memberPermissions?: number;
  /** Whether message comment-threads are allowed here. 1:1 DMs never allow
   *  them; TEXT/ANNOUNCE/GROUP_DM channels toggle it. Default true. */
  commentsEnabled?: boolean;
  /** VOICE: a room whose audio a broadcast director may put on air. Joining one
   *  requires accepting the on-air notice first. Default false. */
  tournament?: boolean;
  /** CATEGORY accent colour (hex like "#ff3b30"). When set, the names of the
   *  channels inside the category render in this colour. Null = default. */
  color?: string | null;
  region: string;
  createdAt: string;
  updatedAt: string;
  overrides?: ChannelPermissionOverride[];
}

export interface DirectChannelParticipant {
  channelId: string;
  userId: string;
  joinedAt: string;
  role: GroupParticipantRole;
  user: {
    id: string;
    platformId: string;
    username: string;
    displayName: string | null;
    avatarUrl: string | null;
    status: UserStatus;
    statusText?: string | null;
    /** cispracs.ru team tag (= the team's slug on the platform). */
    teamTag?: string | null;
  };
}

export interface DirectChannel extends Channel {
  type: 'DM' | 'GROUP_DM';
  directParticipants: DirectChannelParticipant[];
  messages?: Message[];
}

export interface Guild {
  id: string;
  name: string;
  iconUrl: string | null;
  ownerId: string;
  region: string;
  createdAt: string;
  updatedAt: string;
  channels: Channel[];
  /** My guild-wide permission bits (decimal string; ALL for owner/admin).
   *  Present on /api/me, `ready`, `guild:create`, `guild:channels` and the
   *  guild detail; absent on entities another user's view produced. */
  myPermissions?: string;
}

export interface Invite {
  code: string;
  guildId: string;
  channelId: string | null;
  creatorId: string;
  expiresAt: string | null;
  maxUses: number | null;
  uses: number;
  createdAt: string;
}

export interface AuditLogEntry {
  id: string;
  guildId: string;
  actorId: string;
  action: string;
  targetUserId: string | null;
  targetId: string | null;
  reason: string | null;
  metadata: unknown;
  createdAt: string;
  actor: {
    id: string;
    username: string;
    displayName: string | null;
  };
}

// ---------------------------------------------------------------------------
// Current-user / Me DTOs
// ---------------------------------------------------------------------------
// These were previously redeclared in `client/src/lib`'s type file. They are
// wire contracts (server → client over REST), so they live here as the single
// source of truth — the same reason the Message DTOs above moved. The native
// (React Native) client reuses the same `@bunker/shared/api` layer, so the
// shapes it returns must be importable without any web dependency.

export interface MeUser {
  id: string;
  platformId: string;
  username: string;
  displayName: string | null;
  avatarUrl: string | null;
  /** cispracs.ru team tag, normalized upper-case. NULL = no team. */
  teamTag?: string | null;
  /** Full cispracs.ru team name (e.g. "Natus Vincere"). NULL = not provided. */
  teamName?: string | null;
  /** Cispracs ID UI language (amendment 12). NULL = the platform never said. */
  locale?: 'ru' | 'en' | null;
  status: UserStatus;
  /** Who may open a new 1:1 DM with this user. Defaults to EVERYONE. */
  dmPrivacy?: DmPrivacy;
  /** Persist read markers across devices + emit read receipts. Defaults to true. */
  showTeamInDms?: boolean;
  saveReadHistory?: boolean;
  /** First-run onboarding progress (web). NULL/absent = never onboarded → the
   *  welcome tour auto-shows. Set once the user picks a tier or skips. */
  onboardingState?: OnboardingState | null;
  /** Free-form profile bio (≤280). */
  bio?: string | null;
  /** Short custom status under the username (≤64). */
  statusText?: string | null;
  createdAt: string;
}

/** Depth a user picked in the onboarding welcome. `null` = skipped the welcome
 *  (no contextual hints), otherwise drives which just-in-time coachmarks fire. */
export type OnboardingDepth = 'quick' | 'medium' | 'full';

/** Server-persisted onboarding progress. Kept intentionally small — contextual
 *  "hint seen" tracking lives client-side (per-device) to avoid array churn. */
export interface OnboardingState {
  depth: OnboardingDepth | null;
}

/** Response shape of GET /api/me. */
export interface MeResponse {
  user: MeUser;
  guilds: Guild[];
  guildFolders: GuildFolder[];
  dm: DirectChannel[];
  /** The caller's current voice presence, or null when not in a voice channel.
   *  Typed wide (not a `null` literal) so the server can start populating it
   *  without a breaking type change. */
  voiceState: VoiceUserState | null;
}

/** Response shape of POST /api/auth/exchange and /api/auth/test-login (the
 *  parts the client consumes — the `sessionToken` field is read separately by
 *  native clients). */
export interface AuthExchangeResponse {
  ok: true;
  /** Native clients (React Native / Electron) read this and send it as
   *  `Authorization: Bearer`. Absent for browsers (they ride the cookie). */
  sessionToken?: string;
  /** Long-lived rotating refresh token — native clients only. */
  refreshToken?: string;
  user: {
    id: string;
    username: string;
    displayName: string | null;
    avatarUrl: string | null;
  };
}

/** Response from POST /api/auth/refresh — a freshly rotated token pair. */
export interface AuthRefreshResponse {
  ok: true;
  sessionToken: string;
  refreshToken: string;
}

// ---------------------------------------------------------------------------
// Media / Tenor DTOs
// ---------------------------------------------------------------------------

/**
 * Normalised Tenor result delivered by /api/gifs/{trending,search}. The server
 * hides the API key and pre-trims Tenor's verbose media_formats down to the
 * two URLs the picker actually needs.
 */
export interface TenorGif {
  id: string;
  title: string;
  /** Full-quality GIF, sent as the message attachment URL. */
  gifUrl: string;
  /** Lower-resolution preview rendered inside the picker grid. */
  previewUrl: string;
  w: number;
  h: number;
}

// ---------------------------------------------------------------------------
// Guild moderation / role / emoji DTOs (richer than the /api/me Guild summary)
// ---------------------------------------------------------------------------

export interface Emoji {
  id: string;
  guildId: string;
  name: string;
  imageUrl: string;
  createdAt: string;
}

export interface DirectMessageCandidate {
  id: string;
  platformId: string;
  username: string;
  displayName: string | null;
  avatarUrl: string | null;
  status: UserStatus;
}

export interface GuildMemberRow {
  guildId: string;
  userId: string;
  nickname: string | null;
  joinedAt: string;
  timeoutUntil: string | null;
  roleIds: string[];
  user: {
    id: string;
    platformId: string;
    username: string;
    displayName: string | null;
    avatarUrl: string | null;
    status: UserStatus;
    statusText?: string | null;
    /** Surfaces in the MemberPanel popover. */
    bio?: string | null;
    /** cispracs.ru team tag, shown as a badge next to the name. */
    teamTag?: string | null;
    /** Full cispracs.ru team name; shown on the profile card. */
    teamName?: string | null;
  };
}

export interface GuildRole {
  id: string;
  guildId: string;
  name: string;
  color: number;
  position: number;
  permissions: string;
  /** The base @everyone role (id == guildId). Implicit for every member — not
   *  assignable and not deletable; editable only for its permissions/overrides. */
  isEveryone?: boolean;
}

export interface GuildBan {
  guildId: string;
  userId: string;
  moderatorId: string;
  reason: string | null;
  createdAt: string;
  expiresAt: string | null;
  user: {
    id: string;
    platformId: string;
    username: string;
    displayName: string | null;
    avatarUrl: string | null;
  };
  moderator: {
    id: string;
    username: string;
    displayName: string | null;
  };
}

/** Shape of GET /api/guilds/:id (richer than what /api/me returns). */
export interface GuildDetail extends Guild {
  /** The first page of the roster, ordered by user id. */
  members: GuildMemberRow[];
  roles: GuildRole[];
  /** Where `GET /api/guilds/:id/members?after=` continues; null = all loaded. */
  membersNextCursor?: string | null;
  /** Every member, however many pages are loaded. */
  memberCount?: number;
  /** Members holding each role (role id → count), over the whole roster. */
  roleMemberCounts?: Record<string, number>;
}

// ---------------------------------------------------------------------------
// RTC config
// ---------------------------------------------------------------------------

/**
 * ICE server entry returned by GET /api/rtc/config. Structurally compatible
 * with the DOM lib's `RTCIceServer`, but declared here so `@bunker/shared`
 * stays free of `lib.dom` (the native client has no DOM types). The web layer
 * passes these straight into `new RTCPeerConnection({ iceServers })`.
 */
export interface IceServer {
  urls: string | string[];
  username?: string;
  credential?: string;
}

// ---------------------------------------------------------------------------
// Voice presence
// ---------------------------------------------------------------------------

/**
 * Per-user state inside a VOICE channel, broadcast over the socket. Single
 * source of truth for the server's VoiceRoomManager, the web voice store, and
 * the mobile voice roster — previously duplicated in all three.
 */
export interface VoiceUserState {
  userId: string;
  channelId: string;
  selfMute: boolean;
  selfDeaf: boolean;
  serverMute: boolean;
  serverDeaf: boolean;
  videoOn: boolean;
  screenOn: boolean;
  joinedAt: number;
}

/** Fields a user may change about their own voice state. */
export type VoiceStatePatch = Partial<
  Pick<VoiceUserState, 'selfMute' | 'selfDeaf' | 'videoOn' | 'screenOn'>
>;
/** Fields a moderator may change about another user's voice state. */
export type VoiceModerationPatch = Partial<Pick<VoiceUserState, 'serverMute' | 'serverDeaf'>>;
